import { useStore } from "@tanstack/react-form";

import { useFieldContext } from "..";
import { FileInput } from "../../FileInput";
import { getErrorMessage } from "../utils";
import FieldWrapper from "./FieldWrapper";

type FileFieldProps = {
  label?: string;
  subLabel?: string;
  required?: boolean;
  maxSize?: number;
  allowedTypes?: string[];
  accept?: string | string[];
  multiple?: boolean;
  onFilesSelected?: (files: File[]) => void;
  onFileRemove?: (file: File, index: number) => void;
  onPreview?: (file: File, index: number) => void;
  existingFiles?: File[] | undefined;
};

export const FileField = ({
  label,
  subLabel,
  required,
  maxSize = 5 * 1024 * 1024, // 5MB default
  allowedTypes = [".pdf", ".doc", ".docx", "image/*"],
  accept,
  multiple = false,
  onFilesSelected,
  onFileRemove,
  onPreview,
  existingFiles,
}: FileFieldProps) => {
  const field = useFieldContext<File | File[] | undefined>();

  const errors = useStore(field.store, (state) => state.meta.errors);
  const hasError = errors.length > 0 && field.state.meta.isDirty;

  const handleFilesSelected = (files: File[]) => {
    if (multiple) {
      field.handleChange(files);
    } else {
      field.handleChange(files[0] || undefined);
    }

    onFilesSelected?.(files);
  };

  const handleFileRemove = (file: File, index: number) => {
    field.handleChange(undefined);
    onFileRemove?.(file, index);
  };

  return (
    <FieldWrapper
      id={field.name}
      label={label}
      subLabel={subLabel}
      isRequired={required}
      errorMessage={hasError ? getErrorMessage(errors[0]) : undefined}
    >
      <FileInput
        maxSize={maxSize}
        allowedTypes={allowedTypes}
        accept={accept}
        multiple={multiple}
        onFileRemove={handleFileRemove}
        onFilesSelected={handleFilesSelected}
        onPreview={onPreview}
        existingFiles={existingFiles}
      />
    </FieldWrapper>
  );
};
